import React from 'react';
import { pricingPlans } from '../data/mockData';
import { Button } from './ui/button';
import { Check } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PricingSection = ({ onOpenContactForm }) => {
  const header = useScrollAnimation({ threshold: 0.2 });
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#093336]/20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="inline-block bg-[#A5BF23]/10 text-[#A5BF23] px-4 py-1.5 rounded-full text-xs font-bold tracking-[0.2em] mb-4">PRICING</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#F0F4F4] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Brand Name Registration Pricing</h2>
          <p className="text-base text-[#A0B2B2] leading-relaxed">Every package includes licensed attorney support and complete brand name filing with the USPTO.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto items-stretch">
          {pricingPlans.map((plan, i) => <PricingCard key={plan.id} plan={plan} onOpenContactForm={onOpenContactForm} delay={i + 1} />)}
        </div>
        <p className="text-center text-sm text-[#A0B2B2]/50 mt-10">All packages exclude USPTO filing fees ($250-$350 per class), paid to the government.</p>
      </div>
    </section>
  );
};

const PricingCard = ({ plan, onOpenContactForm, delay }) => {
  const card = useScrollAnimation({ threshold: 0.15 });
  return (
    <div ref={card.ref} data-testid={`pricing-card-${plan.id}`}
      className={`group relative flex flex-col rounded-2xl transition-all duration-300 scroll-fade-up scroll-delay-${delay} ${card.isVisible ? 'scroll-visible' : ''} ${
        plan.recommended
          ? 'bg-[#093336] border-2 border-[#A5BF23]/50 scale-[1.03] z-10 shadow-[0_0_40px_rgba(165,191,35,0.1)]'
          : 'bg-[#093336]/60 backdrop-blur-sm border border-white/5 hover:border-[#A5BF23]/30 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(165,191,35,0.08)]'
      } p-8`}>
      {plan.recommended && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-[#A5BF23] text-[#041B1C] text-xs font-bold tracking-[0.15em] px-5 py-1.5 rounded-full">BEST VALUE</span>
        </div>
      )}
      <div className="mb-6 mt-2">
        <h3 className="text-lg font-bold mb-3 text-[#A0B2B2]">{plan.name}</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-bold tracking-tight text-[#F0F4F4]" style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.price}</span>
        </div>
        <p className="text-xs mt-1.5 text-[#A0B2B2]/60">{plan.federal}</p>
      </div>
      <ul className="space-y-3 flex-1 mb-8">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <div className="w-5 h-5 rounded-full bg-[#A5BF23]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Check className="w-3 h-3 text-[#A5BF23]" />
            </div>
            <span className="text-sm text-[#A0B2B2]">{feature}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto">
        <p className="text-[10px] text-center mb-3 text-[#A0B2B2]/40">{plan.note}</p>
        <Button data-testid={`pricing-cta-${plan.id}`} onClick={onOpenContactForm}
          className={`w-full font-semibold h-11 rounded-full transition-all duration-300 hover:-translate-y-0.5 ${
            plan.recommended ? 'bg-[#A5BF23] hover:bg-[#8A9F1D] text-[#041B1C] hover:shadow-[0_8px_32px_rgba(165,191,35,0.3)]' : 'bg-transparent border-2 border-[#A5BF23] text-[#A5BF23] hover:bg-[#A5BF23]/10'
          }`}>
          {plan.buttonText}
        </Button>
      </div>
    </div>
  );
};

export default PricingSection;
